/* eslint-disable prettier/prettier */
import jsPDF from 'jspdf';
import logo from '../../../../../style/img/logo_acciona.png';
import AccionaRegular from '../../../../../style/fonts/acciona-regular-normal';
import AccionaBold from '../../../../../style/fonts/acciona-medium-bold';
import { docility } from '../../../../../utils/utils';
import { calcImagesDimensionsAndUrl, insertGallery } from './util';
require('jspdf-autotable');

const cellStyle = {
  fillColor: 'rgb(230, 230, 230)',
  cellPadding: 8,
  halign: 'left',
  fontSize: 11,
  valign: 'middle',
  lineWidth: 2,
  lineColor: 'black',
  fontStyle: 'bold',
  font: 'AccionaBold',
};

const tableCellStyle = {
  fillColor: 'white',
  cellPadding: 8,
  halign: 'center',
  textColor: '#666666',
  fontSize: 10,
  valign: 'middle',
  lineWidth: 2,
  lineColor: 'black',
  font: 'AccionaRegular',
};

const nullValue = (value, intl) => {
  if (value === null || value === 'null') {
    return intl.formatMessage({ id: 'unregisteredValue' });
  } else {
    return value;
  }
};

const generatePDF = async ({ prm, fileName, intl, images }) => {
  const doc = new jsPDF({
    unit: 'px',
    hotfixes: ['px_scaling'],
    compress: true,
  });

  const pageWidth = doc.internal.pageSize.getWidth() - 110;
  const pageHeight = doc.internal.pageSize.getHeight();

  const onDidDrawCell = (data) => {
    const calculateXPosition = (type, index, imageWidth, cellWidth) => {
      let position = data.cell.x;
      if (cellWidth <= imageWidth) {
        if (index % 2 !== 0) {
          position = position + 2;
        }
      } else if (cellWidth > imageWidth) {
        position = position + (cellWidth - imageWidth) / 2;
      }
      return position;
    };

    const calculateYPosition = (index, type) => {
      let position = data.cell.y;
      let firstElementOfTheRow;
      let lastElementOfTheRow;
      if (index % 2 === 0) {
        firstElementOfTheRow = imagesDimensions[type][index]?.height;
        lastElementOfTheRow = imagesDimensions[type][index + 1]?.height;
      } else {
        firstElementOfTheRow = imagesDimensions[type][index - 1]?.height;
        lastElementOfTheRow = imagesDimensions[type][index]?.height;
      }

      if (firstElementOfTheRow && lastElementOfTheRow) {
        const greatestHeight = Math.max(
          ...[firstElementOfTheRow, lastElementOfTheRow],
        );

        position =
          position +
          (greatestHeight - imagesDimensions[type][index]?.height) / 2;
      }

      if (index === '0' || index === '1') {
        position = position + 10;
      }

      return position;
    };
    if (/prm_q.*|signature/.test(data.cell?.text[0])) {
      const [type, index] = data.cell?.text[0].split('__');
      if (images[type].length) {
        const { width: w, height: h, url } = imagesDimensions[type][index];
        const width = w;
        const height = h;
        const xPosition = calculateXPosition(type, index, w, data.cell?.width);
        const yPosition = calculateYPosition(index, type);

        if (w && h && url) {
          doc.addImage({
            imageData: url,
            x: xPosition,
            y: yPosition,
            width,
            height,
            compression: 'FAST',
            format: type === 'signature' ? 'PNG' : 'JPEG',
          });
        }
      }
    }
  };

  const onDidParseCell = (data) => {
    if (/prm_q.*|signature/.test(data.cell?.text[0])) {
      const [type, index] = data.cell?.text[0].split('__');
      if (images[type].length) {
        const dimensions = imagesDimensions[type][index];
        data.cell.styles = {
          ...data.cell.styles,
          minCellHeight: dimensions.height + 24,
          textColor: '#ffffff',
          valign: 'middle',
          halign: 'center',
        };
      }
    }
  };

  const imagesDimensions = await calcImagesDimensionsAndUrl(images, doc);
  const cellWidth = (doc.internal.pageSize.getWidth() - 110) / 4;
  const columnStyles = {
    0: { cellWidth },
    1: { cellWidth },
    2: { cellWidth },
    3: { cellWidth },
  };
  doc.addFileToVFS('AccionaRegular.ttf', AccionaRegular);
  doc.addFileToVFS('AccionaBold.ttf', AccionaBold);
  doc.addFont('AccionaRegular.ttf', 'AccionaRegular', 'normal');
  doc.addFont('AccionaBold.ttf', 'AccionaBold', 'bold');

  doc.autoTable({
    columnStyles,
    rowPageBreak: 'avoid',
    didDrawCell: onDidDrawCell,
    didParseCell: onDidParseCell,
    body: [
      [
        {
          content: intl.formatMessage({ id: 'n_prm' }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content: nullValue(prm.prm_number, intl),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
        {
          content: intl.formatMessage({ id: 'supplier' }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content:
            prm.pre_delivery_note_data?.model !== 'other'
              ? intl.formatMessage({
                  id: prm.pre_delivery_note_data?.model,
                })
              : '',
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      [
        {
          content: intl.formatMessage({
            id: 'pre_delivery_note_data.arriving_date',
          }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content: nullValue(prm.pre_delivery_note_data.arriving_date, intl),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
        {
          content: intl.formatMessage({ id: 'recipe' }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content: prm.pre_delivery_note_data?.recipe ?? '',
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      [
        {
          content: intl.formatMessage({ id: 'docket_number' }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content: nullValue(prm.pre_delivery_note_data.docket_number, intl),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
        {
          content: intl.formatMessage({ id: 'detailed_location' }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content: nullValue(prm.pre_delivery_note_data?.detailed_location, intl),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      [
        {
          content: '',
          colSpan: 4,
        },
      ],
      [
        {
          content: intl.formatMessage({ id: 'PRM_Q1' }),
          colSpan: 3,
          styles: { ...cellStyle },
        },
        {
          content: intl.formatMessage({ id: 'true' }),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      ...images.prm_q1.map((image, index) => {
        return [
          {
            content: 'prm_q1__' + index,
            colSpan: 4,
            styles: { minCellHeight: 100 },
          },
        ];
      }),
    ],
    margin: { top: 90 },
    useCss: true,
  });

  doc.addPage();

  doc.autoTable({
    rowPageBreak: 'avoid',
    didDrawCell: onDidDrawCell,
    didParseCell: onDidParseCell,
    columnStyles,
    body: [
      [
        {
          content: intl.formatMessage({ id: 'PRM_Q2' }),
          colSpan: 3,
          styles: { ...cellStyle },
        },
        {
          content: intl.formatMessage({ id: String(prm.defects_results) }),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      ...insertGallery(images.prm_q2, 'prm_q2'),
      [
        {
          content: intl.formatMessage({ id: 'Comments' }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content: [
            prm.right_appearance_comments,
            prm.limit_time_comments,
            prm.defects_results_comments,
          ]
            .map((value, index) => (value ? `${index + 1}. ${value}` : null))
            .filter((value) => value)
            .join('\n'),
          colSpan: 3,
          styles: { ...tableCellStyle, halign: 'left' },
        },
      ],
      [
        {
          content: '',
          colSpan: 4,
        },
      ],
    ],
    margin: { top: 90 },
    useCss: true,
  });

  doc.addPage();

  doc.autoTable({
    rowPageBreak: 'avoid',
    didDrawCell: onDidDrawCell,
    didParseCell: onDidParseCell,
    columnStyles,
    body: [
      [
        {
          content: intl.formatMessage({ id: 'PRM_Q3' }),
          colSpan: 3,
          styles: { ...cellStyle },
        },
        {
          content: intl.formatMessage({
            id: String(prm.substance_results),
          }),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      ...insertGallery(images.prm_q3, 'prm_q3'),
      [
        {
          content: intl.formatMessage({ id: 'Comments' }),
          colSpan: 1,
          styles: { ...cellStyle },
        },
        {
          content: [
            prm.added_substance_comments,
            prm.substance_results_comments,
          ]
            .map((value, index) => (value ? `${index + 1}. ${value}` : null))
            .filter((value) => value)
            .join('\n'),
          colSpan: 3,
          styles: { ...tableCellStyle, halign: 'left' },
        },
      ],
      [
        {
          content: '',
          colSpan: 4,
        },
      ],
    ],
    margin: { top: 90 },
    useCss: true,
  });

  doc.addPage();

  doc.autoTable({
    rowPageBreak: 'avoid',
    didDrawCell: onDidDrawCell,
    didParseCell: onDidParseCell,
    columnStyles,
    body: [
      [
        {
          content: intl.formatMessage({ id: 'PRM_Q4' }),
          colSpan: 3,
          styles: { ...cellStyle },
        },
        {
          content: intl.formatMessage({
            id: '0' + String(prm.pre_delivery_note_data.mash_control),
          }),
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      ...(prm.pre_delivery_note_data.specimens_number
        ? [
            [
              {
                content: intl.formatMessage({ id: 'specimens_number' }),
                colSpan: 2,
                styles: { ...cellStyle },
              },
              {
                content: nullValue(prm.pre_delivery_note_data.specimens_number, intl),
                colSpan: 2,
                styles: { ...tableCellStyle },
              },
            ],
            [
              {
                content: intl.formatMessage({ id: 'slump' }),
                colSpan: 2,
                styles: { ...cellStyle },
              },
              {
                content: `${prm.pre_delivery_note_data?.slump}`,
                colSpan: 2,
                styles: { ...tableCellStyle },
              },
            ],
            [
              {
                content: intl.formatMessage({ id: 'consistency' }),
                colSpan: 2,
                styles: { ...cellStyle },
              },
              {
                content: intl.formatMessage({
                  id: docility(prm.pre_delivery_note_data?.slump),
                }),
                colSpan: 2,
                styles: { ...tableCellStyle },
              },
            ],
            ...insertGallery(images.prm_q4, 'prm_q4'),
            [
              {
                content: intl.formatMessage({ id: 'Comments' }),
                colSpan: 1,
                styles: { ...cellStyle },
              },
              {
                content: [prm.specimens_comments]
                  .map((value, index) =>
                    value ? `${index + 1}. ${value}` : null,
                  )
                  .filter((value) => value)
                  .join('\n'),
                colSpan: 3,
                styles: { ...tableCellStyle, halign: 'left' },
              },
            ],
          ]
        : []),
    ],
    margin: { top: 90 },
    useCss: true,
  });

  doc.addPage();

  doc.autoTable({
    rowPageBreak: 'avoid',
    didDrawCell: onDidDrawCell,
    didParseCell: onDidParseCell,
    columnStyles,
    body: [
      [
        {
          content: intl.formatMessage({ id: 'signature' }),
          colSpan: 3,
          styles: { ...cellStyle },
        },
        {
          content: prm.signer || 'Admin',
          colSpan: 1,
          styles: { ...tableCellStyle },
        },
      ],
      ...images.signature.map((image, index) => {
        return [
          {
            content: 'signature__' + index,
            colSpan: 4,
            styles: { minCellHeight: 100 },
          },
        ];
      }),
    ],
    margin: { top: 90 },
    useCss: true,
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (var i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.addImage(logo, 'png', 40, 20, 140, 67);

    const currentPageInfo = doc.getCurrentPageInfo();
    doc.setFont('AccionaRegular');
    doc.setFontSize(12);
    doc.text(
      `${currentPageInfo.pageNumber}/${doc.getNumberOfPages()}`,
      pageWidth + 110 - 75,
      pageHeight - 30,
    );
  }
  doc.save(fileName + '.pdf');
};

export default generatePDF;
